import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import type { ApiTokenDisplay } from 'aliv-shared';

export default function DeveloperApiSection() {
  const [tokens, setTokens] = useState<ApiTokenDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Creation state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [creating, setCreating] = useState(false);

  // Newly created token (shown once)
  const [newToken, setNewToken] = useState<{ token: string; displayName: string } | null>(null);
  const [copied, setCopied] = useState(false);

  // Revoke state
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const loadTokens = useCallback(async () => {
    setError('');
    try {
      const { tokens: list } = await api.listApiTokens();
      setTokens(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API tokens');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTokens();
  }, [loadTokens]);

  async function handleCreate() {
    if (!displayName.trim()) return;
    setCreating(true);
    setError('');
    try {
      const result = await api.createApiToken(displayName.trim());
      setNewToken(result);
      setShowCreateForm(false);
      setDisplayName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create token');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(tokenId: string) {
    if (!confirm('Are you sure you want to revoke this API token? This cannot be undone.')) return;
    setRevokingId(tokenId);
    setError('');
    try {
      await api.deleteApiToken(tokenId);
      setTokens((prev) => prev.filter((t) => t.tokenId !== tokenId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke token');
    } finally {
      setRevokingId(null);
    }
  }

  function handleDismissNewToken() {
    setNewToken(null);
    void loadTokens();
  }

  function handleCopy() {
    if (!newToken) return;
    navigator.clipboard.writeText(newToken.token).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  if (loading) {
    return <p className="settings-loading">Loading API tokens...</p>;
  }

  return (
    <div className="developer-api-section">
      <p className="developer-api-desc">
        Use API tokens to create verification sessions programmatically via the REST API.
      </p>

      {error && <p className="error-msg">{error}</p>}

      {/* Newly created token display */}
      {newToken && (
        <div className="developer-api-new-token">
          <p className="developer-api-new-token-warning">
            This token will only be shown once. Copy it now.
          </p>
          <div className="developer-api-token-value">
            <code>{newToken.token}</code>
            <button type="button" className="btn btn-ghost" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div className="developer-api-curl-example">
            <p className="developer-api-curl-label">Example usage:</p>
            <code>
              {`curl -X POST https://aliv.me/api/sessions \\
  -H "Authorization: Bearer ${newToken.token}" \\
  -H "Content-Type: application/json" \\
  -d '{"candidateName": "Jane Doe"}'`}
            </code>
          </div>
          <button type="button" className="btn btn-primary" onClick={handleDismissNewToken}>
            Done
          </button>
        </div>
      )}

      {/* Token list */}
      {tokens.length > 0 && (
        <div className="developer-api-token-list">
          {tokens.map((t) => (
            <div key={t.tokenId} className="developer-api-token-item">
              <div className="developer-api-token-info">
                <span className="developer-api-token-name">{t.displayName}</span>
                <span className="developer-api-token-meta">
                  Created {formatDate(t.createdAt)}
                  {t.lastUsedAt && <> &middot; Last used {formatDate(t.lastUsedAt)}</>}
                </span>
              </div>
              <button
                type="button"
                className="btn btn-danger"
                disabled={revokingId === t.tokenId}
                onClick={() => void handleRevoke(t.tokenId)}
              >
                {revokingId === t.tokenId ? 'Revoking...' : 'Revoke'}
              </button>
            </div>
          ))}
        </div>
      )}

      {tokens.length === 0 && !newToken && (
        <p className="developer-api-empty">No API tokens yet.</p>
      )}

      {/* Create form */}
      {showCreateForm ? (
        <div className="developer-api-create-form">
          <div className="field">
            <label className="field-label" htmlFor="token-name">
              Token name
            </label>
            <input
              id="token-name"
              className="input"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. CI/CD Pipeline"
              autoComplete="off"
              maxLength={64}
            />
          </div>
          <div className="developer-api-create-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!displayName.trim() || creating}
              onClick={() => void handleCreate()}
            >
              {creating ? 'Creating...' : 'Create Token'}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setShowCreateForm(false);
                setDisplayName('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        !newToken && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowCreateForm(true)}
            style={{ marginTop: 'var(--space-4)' }}
          >
            Create Token
          </button>
        )
      )}
    </div>
  );
}
